import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';

import OnDeleteIcon from '../../icons/OnDeleteIcon';
import {
    selectAttendedStatusId,
    selectDoubtRaisedStatusId,
    selectFailedToAttendStatusId,
    selectNotKnownStatusId,
    selectSanctionedStatusId
} from '../../store/dataSelectors';
import {
    deleteParticipant,
    loadParticipantSectionHeadings,
    loadRecentParticipants,
    saveCurrentParticipantId
} from '../../store/participantService';
import { hasRole, SUPERUSER } from '../../utils/userRoles';
import ParticipantStickyMenu from '../navigation/participant/ParticipantStickyMenu';
import AccordionHeader from '../ui/accordion/AccordionHeader';
import AccordionIcon from '../ui/accordion/AccordionIcon';
import PromptModal from '../ui/modals/PromptModal';

import ActionPlan from './actionPlan/ActionPlan';
import Barriers from './barriers/Barriers';
import BSOP from './bsop/BSOP';
import ConnectToWork from './connectToWork/ConnectToWork';
import Employability from './employability/Employability';
import Environment from './environment/Environment';
import Form from './form/Form';
import LTCP from './ltcp/LTCP';
import NDPPData from './ndppData/NDPPData';
import ParticipantInformation from './participantInformation/ParticipantInformation';
import Circumstances from './personal/circumstances/Circumstances';
import HealthInformation from './personal/healthInformation/HealthInformation';
import PrimaryDetails from './primaryDetails/PrimaryDetails';
import Questionnaire from './questionnaire/Questionnaire';
import T2DR from './t2dr/T2DR';
import Wellbeing from './wellbeing/Wellbeing';

import classes from '../../app.module.css';
import editPt from './ptStyles.module.css';

const deleteRoles = [SUPERUSER];
const deleteMsg = `Participant deleted`;

const EditParticipant = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();

    // LOCAL STATE
    const initialState = {
        participantBarriers: false,
        actionPlanGoals: false,
        serviceStandards: false,
        wellbeing: false,
        workRequirements: false,
        workHistory: false,
        education: false,
        submissions: false,
        healthInformation: false,
        circumstances: false,
        employability: false,
        environment: false,
        connectToWork: false,
        financialRequests: false,
        assetRequests: false,
        ltcp: false,
        t2dr: false,
        ndppData: false,
        bsop: false
    };

    const initialModalData = { promptType: '', content: '', formId: '' };
    const [isSection, setIsSection] = useState(initialState);
    const [showPromptModal, setShowPromptModal] = useState(false);
    const [modalData, setModalData] = useState(initialModalData);
    const [expanded, setExpanded] = useState('primaryDetails');
    const [expandedInnerPanel, setExpandedInnerPanel] = useState('');

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const participantSectionHeadings = useSelector(
        (state) => state.entities.participantService.participantSectionHeadings
    );
    const eligibilityGroupSearch = useSelector(
        (state) => state.entities.participantService.eligibilityGroupSearch
    );
    const notKnownAttendanceStatusId = useSelector(selectNotKnownStatusId);
    const attendedAttendanceStatusId = useSelector(selectAttendedStatusId);
    const doubtRaisedStatusId = useSelector(selectDoubtRaisedStatusId);
    const sanctionedStatusId = useSelector(selectSanctionedStatusId);
    const failedToAttendStatusId = useSelector(selectFailedToAttendStatusId);
    const contractDetailsForContractId = useSelector(
        (state) => state.entities.directusService.contractDetailsForContractId
    );
    const locationsForServices = useSelector(
        (state) => state.entities.directusService.locationsForServices
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        if (!currentParticipant || !('contractId' in currentParticipant)) return;
        dispatch(loadParticipantSectionHeadings(currentParticipant.contractId));
        saveCurrentParticipantId(currentParticipant.id);
        dispatch(loadRecentParticipants());
        setExpanded('primaryDetails');
        setExpandedInnerPanel('');
    }, [currentParticipant]);

    useEffect(() => {
        if (!state) return;
        if (state.accordionPanel === 'circumstances') {
            setExpanded('personalInformation');
            setExpandedInnerPanel('circumstances');
        }
        if (
            state.attendanceStatusId !== notKnownAttendanceStatusId &&
            state.appointmentTypeName === 'Exit Interview' &&
            state.exitFormTemplateId
        ) {
            setModalData({
                promptType: 'Form',
                content:
                    'Your Participant has completed their Exit Interview, please complete the relevant Exit Form',
                formId: state.exitFormTemplateId
            });
            setShowPromptModal(true);
        } else if (
            state.attendanceStatusId === attendedAttendanceStatusId &&
            [doubtRaisedStatusId, sanctionedStatusId].includes(currentParticipant.statusId) &&
            contractDetailsForContractId?.contract_type.name === 'EMPLOYABILITY' &&
            state.complianceActivityFormQuestionnaireId
        ) {
            const appointmentLocation = locationsForServices?.find(
                (location) => location.id === state.appointmentLocationId
            );
            if (!appointmentLocation?.remote) {
                setModalData({
                    promptType: 'Form',
                    content:
                        'Your Participant has completed their Appointment, please complete the relevant Compliance Activity Form Questionnaire',
                    formId: state.complianceActivityFormQuestionnaireId
                });
                setShowPromptModal(true);
            }
        } else if (
            state.attendanceStatusId === failedToAttendStatusId &&
            state.mandatoryAppointment &&
            state.doubtFormId
        ) {
            const eligibilityGroup = eligibilityGroupSearch?.find(
                (el) => el.id === currentParticipant?.eligibilityGroupId
            );
            if (eligibilityGroup?.mandatoryActivity) {
                setModalData({
                    promptType: 'Form',
                    content:
                        'Your Participant has failed to attend a mandatory Appointment. Please complete the Doubt Form',
                    formId: state.doubtFormId
                });
                setShowPromptModal(true);
            }
        }
    }, [state]);

    useEffect(() => {
        if (Object.keys(participantSectionHeadings).length !== 0) {
            // eslint-disable-next-line no-unused-vars
            const { id, contractId, ...rest } = participantSectionHeadings;
            setIsSection(rest);
        }
    }, [participantSectionHeadings]);

    useEffect(() => {
        if (successMessage === deleteMsg) {
            dispatch(loadRecentParticipants());
            navigate('/home');
        }
    }, [successMessage]);

    // EVENT HANDLERS
    const onPanelChange = (panel) => {
        setExpanded(expanded === panel ? '' : panel);
        setExpandedInnerPanel('');
    };

    const onInnerPanelChange = (panel) =>
        setExpandedInnerPanel(expandedInnerPanel === panel ? '' : panel);

    const onModalClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        setShowPromptModal(false);
        setModalData(initialModalData);
        navigate('/mandatory_form', {
            state: { formId: modalData.formId }
        });
    };

    const onDeleteParticipant = () => {
        dispatch(deleteParticipant(currentParticipant.id, deleteMsg));
    };

    // RENDER
    return (
        <div>
            <div className={classes.withSideBar}>
                <div className={editPt.content}>
                    <div className={editPt.headerContent}>
                        <div className={editPt.headerContentLeft}>
                            <h1 className={classes.mainHeading}>Edit Participant</h1>
                            {hasRole(deleteRoles, roles) && (
                                <div>
                                    <OnDeleteIcon
                                        onDelete={onDeleteParticipant}
                                        roles={roles}
                                        acceptedRoles={deleteRoles}
                                        confirmTwice={true}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <Accordion
                        expanded={expanded === 'primaryDetails'}
                        onChange={() => onPanelChange('primaryDetails')}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="panel1a-content"
                            id="panel1a-header">
                            <AccordionHeader>Primary Details</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'primaryDetails' && <PrimaryDetails />}
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expanded === 'participantInformation'}
                        onChange={() => onPanelChange('participantInformation')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="panel2a-content"
                            id="panel2a-header">
                            <AccordionHeader>Participant Information</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'participantInformation' && <ParticipantInformation />}
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expanded === 'personalInformation'}
                        onChange={() => onPanelChange('personalInformation')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="panel3a-content"
                            id="panel3a-header">
                            <AccordionHeader>Personal Information</AccordionHeader>
                        </AccordionSummary>
                        <div hidden={!isSection.healthInformation}>
                            <Accordion
                                expanded={expandedInnerPanel === 'healthInformation'}
                                onChange={() => onInnerPanelChange('healthInformation')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3b-content"
                                    id="panel3b-header">
                                    <AccordionHeader>Health Information</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'healthInformation' && (
                                        <HealthInformation />
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.circumstances}>
                            <Accordion
                                expanded={expandedInnerPanel === 'circumstances'}
                                onChange={() => onInnerPanelChange('circumstances')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3c-content"
                                    id="panel3c-header">
                                    <AccordionHeader>Circumstances</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'circumstances' && <Circumstances />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.employability}>
                            <Accordion
                                expanded={expandedInnerPanel === 'employability'}
                                onChange={() => onInnerPanelChange('employability')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3d-content"
                                    id="panel3d-header">
                                    <AccordionHeader>Employability</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'employability' && <Employability />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.environment}>
                            <Accordion
                                expanded={expandedInnerPanel === 'environment'}
                                onChange={() => onInnerPanelChange('environment')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3e-content"
                                    id="panel3e-header">
                                    <AccordionHeader>Environment</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'environment' && <Environment />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.connectToWork}>
                            <Accordion
                                expanded={expandedInnerPanel === 'connectToWork'}
                                onChange={() => onInnerPanelChange('connectToWork')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3f-content"
                                    id="panel3f-header">
                                    <AccordionHeader>Connect To Work</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'connectToWork' && <ConnectToWork />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.ltcp}>
                            <Accordion
                                expanded={expandedInnerPanel === 'ltcp'}
                                onChange={() => onInnerPanelChange('ltcp')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3g-content"
                                    id="panel3g-header">
                                    <AccordionHeader>LTCP</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'ltcp' && <LTCP />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.t2dr}>
                            <Accordion
                                expanded={expandedInnerPanel === 't2dr'}
                                onChange={() => onInnerPanelChange('t2dr')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3h-content"
                                    id="panel3h-header">
                                    <AccordionHeader>
                                        Type 2 Diabetes Path to Remission (T2DR)
                                    </AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 't2dr' && <T2DR />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.ndppData}>
                            <Accordion
                                expanded={expandedInnerPanel === 'ndppData'}
                                onChange={() => onInnerPanelChange('ndppData')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3i-content"
                                    id="panel3i-header">
                                    <AccordionHeader>
                                        National Diabetes Prevention Programme (NDPP) Data
                                    </AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'ndppData' && <NDPPData />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                        <div hidden={!isSection.bsop}>
                            <Accordion
                                expanded={expandedInnerPanel === 'bsop'}
                                onChange={() => onInnerPanelChange('bsop')}
                                slotProps={{ transition: { mountOnEnter: true } }}>
                                <AccordionSummary
                                    expandIcon={<AccordionIcon />}
                                    aria-controls="panel3j-content"
                                    id="panel3j-header">
                                    <AccordionHeader>BSOP</AccordionHeader>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {expandedInnerPanel === 'bsop' && <BSOP />}
                                </AccordionDetails>
                            </Accordion>
                        </div>
                    </Accordion>
                    <div hidden={!isSection.participantBarriers}>
                        <Accordion
                            expanded={expanded === 'participantBarriers'}
                            onChange={() => onPanelChange('participantBarriers')}
                            slotProps={{ transition: { mountOnEnter: true } }}>
                            <AccordionSummary
                                expandIcon={<AccordionIcon />}
                                aria-controls="panel4a-content"
                                id="panel4a-header">
                                <AccordionHeader>Participant Barriers</AccordionHeader>
                            </AccordionSummary>
                            <AccordionDetails>
                                {expanded === 'participantBarriers' && <Barriers />}
                            </AccordionDetails>
                        </Accordion>
                    </div>
                    <div hidden={!isSection.actionPlanGoals}>
                        <Accordion
                            expanded={expanded === 'actionPlan'}
                            onChange={() => onPanelChange('actionPlan')}
                            slotProps={{ transition: { mountOnEnter: true } }}>
                            <AccordionSummary
                                expandIcon={<AccordionIcon />}
                                aria-controls="panel5a-content"
                                id="panel5a-header">
                                <AccordionHeader>Action Plan</AccordionHeader>
                            </AccordionSummary>
                            <AccordionDetails>
                                {expanded === 'actionPlan' && <ActionPlan />}
                            </AccordionDetails>
                        </Accordion>
                    </div>
                    <div hidden={!isSection.serviceStandards}>
                        <Accordion
                            expanded={expanded === 'serviceStandards'}
                            onChange={() => onPanelChange('serviceStandards')}
                            slotProps={{ transition: { mountOnEnter: true } }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel6a-content"
                                id="panel6a-header">
                                <Typography>Service Standards</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>
                                    Panel will be be populated with information from PPL-307: Main
                                    PT - Service Standards.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </div>
                    <div hidden={!isSection.wellbeing}>
                        <Accordion
                            expanded={expanded === 'wellbeing'}
                            onChange={() => onPanelChange('wellbeing')}
                            slotProps={{ transition: { mountOnEnter: true } }}>
                            <AccordionSummary
                                expandIcon={<AccordionIcon />}
                                aria-controls="panel7a-content"
                                id="panel7a-header">
                                <AccordionHeader>Wellbeing</AccordionHeader>
                            </AccordionSummary>
                            <AccordionDetails>
                                {expanded === 'wellbeing' && <Wellbeing />}
                            </AccordionDetails>
                        </Accordion>
                    </div>
                    <Accordion
                        expanded={expanded === 'questionnaire'}
                        onChange={() => onPanelChange('questionnaire')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="panel26a-content"
                            id="panel26a-header">
                            <AccordionHeader>Questionnaire</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>
                            {expanded === 'questionnaire' && <Questionnaire />}
                        </AccordionDetails>
                    </Accordion>
                    <Accordion
                        expanded={expanded === 'form'}
                        onChange={() => onPanelChange('form')}
                        slotProps={{ transition: { mountOnEnter: true } }}>
                        <AccordionSummary
                            expandIcon={<AccordionIcon />}
                            aria-controls="panel26a-content"
                            id="panel26a-header">
                            <AccordionHeader>Form</AccordionHeader>
                        </AccordionSummary>
                        <AccordionDetails>{expanded === 'form' && <Form />}</AccordionDetails>
                    </Accordion>
                </div>
                <div className={classes.sideBar}>
                    <ParticipantStickyMenu />
                </div>
            </div>
            {showPromptModal && (
                <PromptModal
                    isOpen={showPromptModal}
                    promptType={modalData.promptType}
                    content={modalData.content}
                    onClose={onModalClose}
                />
            )}
        </div>
    );
};

export default EditParticipant;
